import { CarTypesEnum } from '@/components/car-details-v2/car-type';
import Typography from '@/components/shared/typography/typography.component';
import useTranslationHook from '@/hook/use-translation-client.hook';
import { CarDetailsType } from '@/types';
import { numberWithCommas } from '@/utils';
import { TFunction } from 'i18next';

export const PriceBreakDownDetails = ({
    carData,
    carType,
    t,
}: {
    carData: CarDetailsType;
    carType: CarTypesEnum;
    t: TFunction;
}) => {
    return (
        <>
            <div className="w-full h-full flex flex-col items-center">
                <div className="flex flex-col w-full h-full py-4 px-3 border border-gray-200 rounded-xl">
                    <div>
                        <div className="flex justify-between">
                            <Typography className="text-gray-700 text-body2Medium md:text-body1Medium">
                                {t('CarProfile.PriceBreakDown.basePrice')}
                            </Typography>
                            <div className="flex gap-0.5 items-baseline en:flex-row-reverse">
                                <div dir="ltr">
                                    <Typography
                                        className="text-gray-800 text-body2Medium md:text-body1Medium"
                                        component="span"
                                        style={{ fontfamily: 'Poppins' }}
                                    >
                                        {numberWithCommas(
                                            carData.basePrice +
                                                carData.warrantyPrice -
                                                carData.carVatPrice || 0
                                        )}
                                    </Typography>
                                </div>
                                <div>
                                    <Typography
                                        variant="caption2"
                                        className="text-gray-800"
                                    >
                                        {t('EGP')}
                                    </Typography>
                                </div>
                            </div>
                        </div>
                        <div className="w-full border-t-2 border-dashed border-gray-200 my-3" />
                    </div>

                    <div className="flex-1 flex flex-col gap-3">
                        <CarPriceItem
                            text={t('CarProfile.PriceBreakDown.ownershipFees')}
                            value={numberWithCommas(
                                carData.registrationFees +
                                    carData.carVatPrice || 0
                            )}
                        />

                        {carType === CarTypesEnum.CERTIFIED && (
                            <>
                                <CarPriceItem
                                    text={t(
                                        'CarProfile.PriceBreakDown.warranty'
                                    )}
                                    value={t(
                                        'CarProfile.PriceBreakDown.included'
                                    )}
                                />
                                <CarPriceItem
                                    text={t(
                                        'CarProfile.PriceBreakDown.refubrishFees'
                                    )}
                                    value={t(
                                        'CarProfile.PriceBreakDown.included'
                                    )}
                                />
                            </>
                        )}

                        {carType !== CarTypesEnum.Dealer && (
                            <CarPriceItem
                                text={t('CarProfile.PriceBreakDown.sylndrFees')}
                                value={numberWithCommas(+10000)}
                            />
                        )}
                    </div>

                    <div>
                        <div className="border-b border-gray-200 my-4" />
                        <div className="flex justify-between">
                            <Typography className="text-gray-700 text-body2SemiBold md:text-body1SemiBold ">
                                {t('CarProfile.PriceBreakDown.total')}
                            </Typography>
                            <div className="flex gap-0.5 items-baseline en:flex-row-reverse">
                                <div dir="ltr">
                                    <Typography
                                        className="text-gray-800 text-body2SemiBold md:text-body1SemiBold"
                                        component="span"
                                        style={{ fontfamily: 'Poppins' }}
                                    >
                                        {numberWithCommas(
                                            +carData.discountPrice > 0
                                                ? carData.discountPrice
                                                : carData.sylndrSellingPrice
                                        )}
                                    </Typography>
                                </div>
                                <div>
                                    <Typography
                                        variant="caption2"
                                        className="text-gray-800"
                                    >
                                        {t('EGP')}
                                    </Typography>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

const CarPriceItem = ({ text, value }: { text: string; value: string }) => {
    const { t } = useTranslationHook();
    const isNumber = !isNaN(Number(value.replace(/,/g, '')));
    return (
        <div className="flex justify-between items-center">
            <div className="flex gap-1">
                <span className="text-gray-500 text-sm">•</span>
                <Typography variant="caption" className="text-gray-500">
                    {text}
                </Typography>
            </div>
            {!isNumber ? (
                <Typography variant="caption" className="text-gray-800">
                    {value}
                </Typography>
            ) : (
                <div className="flex gap-0.5 items-baseline en:flex-row-reverse">
                    <div dir="ltr">
                        <Typography
                            variant="caption"
                            className="text-gray-800"
                            style={{ fontfamily: 'Poppins' }}
                            component="span"
                        >
                            {value}
                        </Typography>
                    </div>
                    <div>
                        <Typography
                            variant="caption2"
                            className="text-gray-800"
                        >
                            {t('EGP')}
                        </Typography>
                    </div>
                </div>
            )}
        </div>
    );
};
